import React, { useEffect, useState } from 'react';
import { alpha, Box, Collapse, Stack, Typography } from '@mui/material';
import { DrawerPopup } from 'components/UI/DrawerPopup';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { DateCalendar } from '@mui/x-date-pickers/DateCalendar';
import { Check, ChevronDown } from 'lucide-react';
import type { DateRangeKey } from 'components/pages/certificates/CertificatesDateRangeFilter';
import { border } from 'settings/tokens';
import { FilterSheetFooter } from 'components/UI/FilterSheetFooter';
import { FIELD_RADIUS_MAX } from 'components/UI/fieldMetrics';
import { menuRowToneSx, MENU_ROW_LABEL_SX } from 'components/UI/menuRowTone';
import { format } from 'date-fns';

export interface MobileDateRangeDrawerProps {
  open: boolean;
  /**
   * REQUIRED — the sheet's heading, e.g. "Scan date".
   *
   * It used to be optional with a default of `'Default'`, which is how
   * ProvenanceFilter shipped a sheet literally titled "Default": a placeholder
   * default cannot be told apart from a real value at the call site OR at a
   * glance in the UI, so the one thing that would have caught it — a missing
   * prop — was the thing the default suppressed. Required means a new call site
   * cannot forget it, and TypeScript says so at the call site rather than the
   * user finding out.
   */
  title: string;
  selectedRange: DateRangeKey;
  selectedDate: Date | null;
  onSelectRange: (key: NonNullable<DateRangeKey>) => void;
  onSelectDate: (date: Date | null) => void;
  onClear: () => void;
  onDone: () => void;
  onClose: () => void;
}

export const MobileDateRangeDrawer: React.FC<MobileDateRangeDrawerProps> = ({
  open,
  title,
  selectedRange,
  selectedDate,
  onSelectRange,
  onSelectDate,
  onClear,
  onDone,
  onClose,
}) => {
  const presets = [
    { key: 'last_month', label: 'Last month' },
    { key: 'last_3m', label: 'Last 3 months' },
    { key: 'last_6m', label: 'Last 6 months' },
    { key: 'last_12m', label: 'Last 12 months' },
  ] as const;

  /*
   * `isCustom` is DERIVED, not a fifth stored value. A date being set is exactly
   * what "custom" means here, and the parent already clears `selectedRange` when
   * one is picked — a separate flag could disagree with that and would be the
   * kind of duplicated state that drifts.
   */
  const isCustom = Boolean(selectedDate)
  const [showCalendar, setShowCalendar] = useState(isCustom)

  /* Reopen the calendar if the sheet is reopened on a custom date — collapsed
     over a date the reader already chose would hide their own answer. */
  useEffect(() => {
    if (isCustom) setShowCalendar(true)
  }, [isCustom])

  return (
    <DrawerPopup
      title={title}
      rightActionLabel='Done'
      rightActionOnClick={onDone}
      anchor={'bottom'}
      handleOpen={open}
      handleClose={onClose}
      // Content-height, not full-page (VERA-294 rule; ported from VERA-315).
      paperSx={{ height: 'auto', maxHeight: '85vh' }}
      child={
        <Stack sx={{ height: '100%' }}>
          <Stack sx={{ p: 2 }} spacing={2}>
            {/*
              SINGLE-SELECT, not checkboxes.

              These four are mutually exclusive — the code already clears the
              others on pick — but a checkbox promises a combination that cannot
              exist, and four of them read as "choose any". A radio says what the
              control actually does before you touch it.

              A selected preset also gets the brand tint + border, so the choice
              is legible at a glance rather than only from an 18px glyph.
            */}
            <Stack spacing={1} role='radiogroup' aria-label={title}>
              {presets.map(opt => {
                const selected = selectedRange === opt.key
                return (
                  <Stack
                    key={opt.key}
                    direction='row'
                    alignItems='center'
                    spacing={1.5}
                    role='radio'
                    aria-checked={selected}
                    tabIndex={0}
                    onClick={() => onSelectRange(opt.key)}
                    onKeyDown={e => {
                      if (e.key === 'Enter' || e.key === ' ') {
                        e.preventDefault()
                        onSelectRange(opt.key)
                      }
                    }}
                    /*
                     * The BORDER + tint keep carrying selection — these rows are a
                     * radio group, and a border is what says "chosen" at a glance.
                     * What was missing is the TEXT half of the contract: both
                     * states drew in full-contrast black, and the row had no hover
                     * or focus response at all, so a tappable row was
                     * indistinguishable from a static one until you pressed it.
                     *
                     * `menuRowToneSx` supplies rest / selected / hover text colour
                     * (`components/UI/menuRowTone`). A SELECTED row keeps its brand
                     * tint on hover — deepened a step rather than replaced by the
                     * shared grey wash, which would read as losing the selection.
                     */
                    sx={theme => {
                      const tone = menuRowToneSx(theme, { selected })
                      return {
                        px: 2,
                        py: 1.25,
                        cursor: 'pointer',
                        borderRadius: `${FIELD_RADIUS_MAX}px`,
                        // `!important` shorthand: the shadow root's global
                        // `border-color` rule outranks an authored `borderColor`.
                        border: `1px solid ${
                          selected ? theme.palette.primary.main : border.subtle
                        } !important`,
                        backgroundColor: 'transparent',
                        ...tone,
                        transition:
                          'color .15s ease, background-color .15s ease, border-color .15s ease',
                        ...(selected && {
                          backgroundColor: alpha(theme.palette.primary.main, 0.06),
                          '&:hover, &:focus-visible': {
                            ...tone['&:hover, &:focus-visible'],
                            backgroundColor: alpha(theme.palette.primary.main, 0.1),
                          },
                        }),
                      }
                    }}
                  >
                    {/*
                      A right-aligned CHECK, not a MUI Radio. The Radio's SVG does
                      not render inside this app's shadow root, so the row showed
                      an indent and nothing in it — worse than the checkboxes it
                      replaced, because at least those were visible. The check is
                      what the desktop popover for this same filter already uses,
                      so the two now agree as a side effect.

                      Selection is still carried by the tint + border, which is
                      what reads at a glance; the check confirms it.
                    */}
                    <Typography sx={{ fontSize: 16, flex: 1, ...MENU_ROW_LABEL_SX }}>
                      {opt.label}
                    </Typography>
                    {/* No `color` — lucide defaults to `currentColor`, so the tick
                        already travels with the row's colour. */}
                    {selected && <Check size={18} />}
                  </Stack>
                )
              })}
            </Stack>

            {/*
              The calendar is COLLAPSED behind this row, and the row is part of the
              same single-select group above it.

              Two things were wrong with showing it always. It made the sheet's
              tallest element a full month grid for what is usually a coarse
              choice, pushing Done off-screen. And it sat below a divider as an
              apparently independent control, while the code clears the preset the
              moment you pick a date and clears the date the moment you pick a
              preset — a relationship the UI never showed. Making "Custom date" a
              fifth option in the same group states the exclusivity that already
              existed.
            */}
            <Stack
              direction='row'
              alignItems='center'
              justifyContent='space-between'
              role='radio'
              aria-checked={isCustom}
              aria-expanded={showCalendar}
              tabIndex={0}
              onClick={() => setShowCalendar(v => !v)}
              onKeyDown={e => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault()
                  setShowCalendar(v => !v)
                }
              }}
              /* Same treatment as the four presets above — it is the fifth option
                 in the same group, so it must not read differently. */
              sx={theme => {
                const tone = menuRowToneSx(theme, { selected: isCustom })
                return {
                  px: 2,
                  py: 1.25,
                  cursor: 'pointer',
                  borderRadius: `${FIELD_RADIUS_MAX}px`,
                  border: `1px solid ${
                    isCustom ? theme.palette.primary.main : border.subtle
                  } !important`,
                  backgroundColor: 'transparent',
                  ...tone,
                  transition:
                    'color .15s ease, background-color .15s ease, border-color .15s ease',
                  ...(isCustom && {
                    backgroundColor: alpha(theme.palette.primary.main, 0.06),
                    '&:hover, &:focus-visible': {
                      ...tone['&:hover, &:focus-visible'],
                      backgroundColor: alpha(theme.palette.primary.main, 0.1),
                    },
                  }),
                }
              }}
            >
              <Stack direction='row' alignItems='center' spacing={1.5} sx={{ flex: 1 }}>
                <Typography sx={{ fontSize: 16, ...MENU_ROW_LABEL_SX }}>
                  {/* The chosen date IS the label once there is one — a row reading
                      "Custom date" beside a selected radio would make the reader
                      open it again just to see what they picked. */}
                  {selectedDate ? format(selectedDate, 'MMM d, yyyy') : 'Custom date'}
                </Typography>
              </Stack>
              {isCustom && <Check size={18} />}
              {/* No `color` — lucide defaults to `currentColor`, so the chevron
                  travels with the row like the check beside it does. It was pinned
                  to `#7E7E7E`, which read as correct only because it happened to sit
                  a couple of values off the row's own muted `#7A7A7A`; on a SELECTED
                  or hovered row it stayed grey while everything around it moved. */}
              <ChevronDown
                size={18}
                style={{
                  transform: showCalendar ? 'rotate(180deg)' : 'none',
                  transition: 'transform .2s ease',
                }}
              />
            </Stack>

            <Collapse in={showCalendar} unmountOnExit>
            <LocalizationProvider dateAdapter={AdapterDateFns}>
              <DateCalendar
                value={selectedDate}
                onChange={d => onSelectDate(d)}
                sx={{
                  width: '100%',
                  maxWidth: '100%',
                  '& .MuiPickersDay-root': { borderRadius: '8px' },
                  '& .MuiDayCalendar-weekDayLabel': { color: 'text.secondary' },
                  '& .MuiPickersCalendarHeader-root .MuiIconButton-root': {
                    backgroundColor: 'transparent',
                    color: 'text.primary',
                  },
                  '& .MuiPickersCalendarHeader-root .MuiIconButton-root:hover': {
                    backgroundColor: 'transparent',
                  },
                  '& .MuiPickersCalendarHeader-root': { px: 1 },
                  '& .MuiDayCalendar-header': { px: 1 },
                  '& .MuiDayCalendar-monthContainer': { margin: 0 },
                  '& .MuiPickersSlideTransition-root': { overflowX: 'hidden', overflowY: 'auto' },
                  '& .MuiYearCalendar-root': {
                    width: 'auto',
                    maxWidth: 'none',
                    boxSizing: 'border-box',
                    paddingLeft: 1,
                    paddingRight: 1,
                  },
                  '& .MuiMonthCalendar-root': {
                    width: 'auto',
                    maxWidth: 'none',
                    boxSizing: 'border-box',
                    paddingLeft: 1,
                    paddingRight: 1,
                    maxHeight: 280,
                    overflowY: 'auto',
                  },
                  '& .MuiPickersDay-today': {
                    border: '1px solid hsla(271, 81%, 56%, 1)',
                    borderRadius: '8px',
                  },
                  '& .MuiPickersDay-root.Mui-selected': {
                    backgroundColor: 'hsla(0, 0%, 0%, 1)',
                    color: 'hsla(0, 0%, 100%, 1)',
                    '&:hover': { backgroundColor: 'hsla(0, 0%, 0%, 1)' },
                    '&:focus': { backgroundColor: 'hsla(0, 0%, 0%, 1)' },
                  },
                }}
              />
            </LocalizationProvider>
            </Collapse>
          </Stack>

          {/*
            The SHARED sheet footer, in its reset-only mode — the same as every
            other filter and sort sheet in the app now.

            It briefly carried `onApply={onDone}`, added so this drawer would
            "commit the way every filter and sort sheet does" back when those
            sheets had an Apply. They no longer do, and that Apply was a duplicate
            even then: the header's `rightActionLabel='Done'` is wired to the SAME
            `onDone`, so the sheet offered two controls, 400px apart, performing
            one action under two names.

            Done stays in the header, because unlike a filter sheet this IS a
            picker with a real commit point — it promotes the staged date into the
            filter sheet behind it (see `ProvenanceFilter`'s `temp*` state). What
            went is the second name for it.
          */}
          <FilterSheetFooter onReset={onClear} />
        </Stack>
      }
    />
  );
};

export default MobileDateRangeDrawer;
